import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  type CanvasThreadListItem,
  usePrivateCanvasThread,
  usePrivateCanvasThreadsList,
  usePrivateCreateCanvasThreadMutation,
  usePrivateUpdateCanvasThreadMutation,
} from '../api/threads';
import { findBlankThread } from '../api/threads/findBlankThread';
import { useChatComposerStore } from '../store/useChatComposerStore';
import { useChatStore } from '../store/useChatStore';
import { useLastThreadStore } from '../store/useLastThreadStore';

type UseThreadManagementParams = {
  resourceId: string;
  workspaceId: string;
  /** Called when resourceId is empty (draft mode). Must create the resource and return the real ID. */
  resolveResourceId?: () => Promise<string>;
};

export type CreateThreadResult = {
  thread: CanvasThreadListItem;
  /** The resourceId used to create the thread. During draft→real transitions this is the newly resolved ID. */
  resolvedResourceId: string;
};

export type UseThreadManagementReturn = {
  createThread: () => Promise<CreateThreadResult>;
  currentThreadId: string | null;
  isCurrentThreadHydrated: boolean;
  isLoadingThreads: boolean;
  setCurrentThreadId: (threadId: string) => void;
  threadError: Error | null;
  threads: CanvasThreadListItem[];
};

export function useThreadManagement({
  resourceId,
  workspaceId,
  resolveResourceId,
}: UseThreadManagementParams): UseThreadManagementReturn {
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(null);
  // API hooks
  const threadsQuery = usePrivateCanvasThreadsList({ canvasId: resourceId, workspaceId });
  const { mutateAsync: createThread } = usePrivateCreateCanvasThreadMutation();
  const { mutate: updateThread } = usePrivateUpdateCanvasThreadMutation();

  // On initial load, select the first available thread.
  // Track which canvas we initialized for so we only auto-select once per canvas
  // (not on list invalidation after thread creation) and reset when the canvas changes.
  const initializedForCanvas = useRef<string | null>(null);

  const persistAndSetThread = useCallback(
    (threadId: string, forResourceId?: string, clearPendingSkillInstructions = true) => {
      if (clearPendingSkillInstructions) {
        useChatComposerStore.getState().clearPendingSkillInstructions();
      }
      setCurrentThreadId(threadId);
      useLastThreadStore.getState().setLastThread(forResourceId ?? resourceId, threadId);
    },
    [resourceId],
  );

  // Canvas changed — clear selection synchronously (during render) so downstream
  // hooks never see a stale threadId paired with the new canvasId.
  const [prevResourceId, setPrevResourceId] = useState(resourceId);

  if (prevResourceId !== resourceId) {
    setPrevResourceId(resourceId);
    // Don't clear thread selection when committing a draft (empty → real ID).
    // The thread was just created for this canvas and should survive the transition.
    if (prevResourceId !== '') {
      useChatComposerStore.getState().clearPendingSkillInstructions();
      setCurrentThreadId(null);
      initializedForCanvas.current = null;
    }
  }

  useEffect(() => {
    if (initializedForCanvas.current !== null || threadsQuery.isLoading || !threadsQuery.data) {
      return;
    }

    const allThreads = threadsQuery.data.pages.flatMap((page) => page.data);

    if (allThreads.length > 0) {
      const lastThreadId = useLastThreadStore.getState().getLastThread(resourceId);
      const lastThread = lastThreadId ? allThreads.find((t) => t.id === lastThreadId) : undefined;
      persistAndSetThread(lastThread ? lastThread.id : allThreads[0].id);
    }

    initializedForCanvas.current = resourceId;
  }, [persistAndSetThread, resourceId, threadsQuery.isLoading, threadsQuery.data]);

  const threadQuery = usePrivateCanvasThread({
    canvasId: resourceId,
    id: currentThreadId ?? undefined,
    workspaceId,
  });

  // Hydrate the chat store with server-persisted messages when a thread is selected.
  // Only hydrate from S3 if the store has no messages for this thread yet.
  // Once hydrated (or once the agent has written messages), the store is the
  // source of truth for the remainder of the session. This prevents:
  // 1. Overwriting agent-updated messages when switching back to a thread
  //    (React Query cache is stale due to staleTime: Infinity)
  // 2. Wiping in-flight messages on a newly created thread before S3
  //    persistence completes (S3 returns 404 → empty array)
  useEffect(() => {
    if (currentThreadId && threadQuery.data) {
      const existingMessages = useChatStore.getState().threads[currentThreadId]?.messages;

      if (!existingMessages || existingMessages.length === 0) {
        useChatStore.getState().setMessagesForThread(currentThreadId, threadQuery.data.messages);
      }

      // Hydrate agent state from S3 if available (new response format)
      if (threadQuery.data.state) {
        useChatStore.getState().setStateForThread(currentThreadId, threadQuery.data.state);
      }
    }
  }, [currentThreadId, threadQuery.data]);

  // Sync sessionTitle from agent state to server (only when it actually changes).
  // Skip auto-update if the thread already has a title (set by agent previously or renamed by user).
  const prevSessionTitleRef = useRef<string | null>(null);
  useEffect(() => {
    if (!currentThreadId) {
      return;
    }
    prevSessionTitleRef.current = null;

    const unsubscribe = useChatStore.subscribe((state) => {
      const sessionTitle = state.threads[currentThreadId]?.state?.sessionTitle ?? null;

      if (sessionTitle && sessionTitle !== prevSessionTitleRef.current) {
        prevSessionTitleRef.current = sessionTitle;

        // If the thread already has a title, don't overwrite it
        const allThreads = threadsQuery.data?.pages.flatMap((page) => page.data) ?? [];
        const currentThread = allThreads.find((t) => t.id === currentThreadId);

        if (currentThread?.title) {
          return;
        }

        updateThread({ canvasId: resourceId, id: currentThreadId, title: sessionTitle, workspaceId });
      }
    });

    return () => unsubscribe();
  }, [currentThreadId, resourceId, threadsQuery.data, updateThread, workspaceId]);

  const threads = useMemo(() => threadsQuery.data?.pages.flatMap((page) => page.data) ?? [], [threadsQuery.data]);

  // True once S3 messages for the current thread have been fetched and hydrated into the store.
  // Used to gate agent creation so it initializes with the correct message history.
  const isCurrentThreadHydrated = !currentThreadId || !!threadQuery.data;

  // Guarded thread creation: `ensureThreadId` in AgentProvider calls this when
  // no currentThreadId exists (e.g. on first message send). Without guards, that
  // would create duplicate blank threads if threads already exist but haven't been
  // auto-selected yet (race during initial load). Instead, we reuse an existing
  // blank thread or fall back to the first thread.
  const creatingThreadRef = useRef<Promise<CreateThreadResult> | null>(null);
  const guardedCreateThread = useCallback(async (): Promise<CreateThreadResult> => {
    if (creatingThreadRef.current) {
      return creatingThreadRef.current;
    }

    creatingThreadRef.current = (async () => {
      try {
        if (threads.length > 0) {
          const blankThread = await findBlankThread(threads, { canvasId: resourceId, workspaceId });

          if (blankThread) {
            persistAndSetThread(blankThread.id, undefined, false);
            return { resolvedResourceId: resourceId, thread: blankThread };
          }

          // Every thread has messages — reuse the first rather than accumulating blank threads
          persistAndSetThread(threads[0].id, undefined, false);
          return { resolvedResourceId: resourceId, thread: threads[0] };
        }

        // In draft mode resourceId is empty; resolve it before creating the first thread.
        let resolvedId = resourceId;

        if (!resolvedId && resolveResourceId) {
          resolvedId = await resolveResourceId();
        }
        const thread = await createThread({ canvasId: resolvedId, workspaceId });
        persistAndSetThread(thread.id, resolvedId, false);
        return { resolvedResourceId: resolvedId, thread };
      } finally {
        creatingThreadRef.current = null;
      }
    })();

    return creatingThreadRef.current;
  }, [threads, createThread, persistAndSetThread, resourceId, resolveResourceId, workspaceId]);

  const setCurrentThreadIdForUser = useCallback(
    (threadId: string, forResourceId?: string) => {
      persistAndSetThread(threadId, forResourceId, true);
    },
    [persistAndSetThread],
  );

  return {
    createThread: guardedCreateThread,
    currentThreadId,
    isCurrentThreadHydrated,
    isLoadingThreads: threadsQuery.isLoading,
    setCurrentThreadId: setCurrentThreadIdForUser,
    threadError: threadsQuery.error ?? threadQuery.error ?? null,
    threads,
  };
}
